import { useQueryClient, useQuery, useMutation, UseMutationOptions } from '@tanstack/react-query';

import { deleteRequest, getRequest, postRequest, putRequest } from 'services/apiRequests';
import type { StringifiedUUID } from 'types';
import { SPMModel, SPMModelRequest } from 'types/SPM';
import type { APIError } from 'utils/errors';
import { QueryHookOptions } from 'types/ReactQuery';

const apiUrl = `/api/v1/spm/models/`;

const getSPMModel = (spmModelId: StringifiedUUID) => getRequest<null, SPMModel>(`${apiUrl}${spmModelId}/`);

const useGetSPMModelQuery = (spmModelId: StringifiedUUID, options: QueryHookOptions<SPMModel, APIError> = {}) => {
  const QUERY_KEY = ['spm-models', spmModelId];
  const queryClient = useQueryClient();

  const query = useQuery<SPMModel, APIError>({
    ...options,
    queryKey: QUERY_KEY,
    queryFn: () => getSPMModel(spmModelId),
  });
  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

const createSPMModel = (csrfToken: string) => (spmModel: SPMModelRequest) => {
  return postRequest<SPMModelRequest, SPMModel>(apiUrl, spmModel, csrfToken);
};

const useCreateSPMModelMutation = (
  csrfToken: string,
  options: UseMutationOptions<SPMModel, APIError, SPMModelRequest> = {},
) => {
  return {
    mutation: useMutation<SPMModel, APIError, SPMModelRequest>({ ...options, mutationFn: createSPMModel(csrfToken) }),
  };
};

type UpdateSPMModelProps = {
  spmModelId: StringifiedUUID;
  spmModel: SPMModelRequest;
};

const updateSPMModel =
  (csrfToken: string) =>
  ({ spmModelId, spmModel }: UpdateSPMModelProps) => {
    return putRequest<SPMModelRequest, SPMModel>(`${apiUrl}${spmModelId}/`, spmModel, csrfToken);
  };

const useUpdateSPMModelMutation = (
  csrfToken: string,
  options: UseMutationOptions<SPMModel, APIError, UpdateSPMModelProps> = {},
) => {
  return {
    mutation: useMutation<SPMModel, APIError, UpdateSPMModelProps>({
      ...options,
      mutationFn: updateSPMModel(csrfToken),
    }),
  };
};

const deleteSPMModel = (csrfToken: string) => (spmModelId: StringifiedUUID) => {
  return deleteRequest<null, void>(`${apiUrl}${spmModelId}/`, null, csrfToken);
};

const useDeleteSPMModelMutation = (
  csrfToken: string,
  options: UseMutationOptions<void, APIError, StringifiedUUID> = {},
) => {
  return {
    mutation: useMutation<void, APIError, StringifiedUUID>({ ...options, mutationFn: deleteSPMModel(csrfToken) }),
  };
};

type UpdateOutputWorksheetOrderProps = {
  spmModelId: StringifiedUUID;
  outputWorksheetOrder: StringifiedUUID[];
};

const updateOutputWorksheetOrder =
  (csrfToken: string) =>
  ({ spmModelId, outputWorksheetOrder }: UpdateOutputWorksheetOrderProps) => {
    return putRequest<{ output_worksheet_order: StringifiedUUID[] }, SPMModel>(
      `${apiUrl}${spmModelId}/output-worksheet-order/`,
      { output_worksheet_order: outputWorksheetOrder },
      csrfToken,
    );
  };

const useUpdateOutputWorksheetOrderMutation = (
  csrfToken: string,
  options: UseMutationOptions<SPMModel, APIError, UpdateOutputWorksheetOrderProps> = {},
) => {
  return {
    mutation: useMutation<SPMModel, APIError, UpdateOutputWorksheetOrderProps>({
      ...options,
      mutationFn: updateOutputWorksheetOrder(csrfToken),
    }),
  };
};

export const SPMModelsService = {
  apiUrl,
  useGetSPMModelQuery,
  useCreateSPMModelMutation,
  useUpdateSPMModelMutation,
  useDeleteSPMModelMutation,
  useUpdateOutputWorksheetOrderMutation,
};
